/**
 * Onboarding v2 state machine (screenshots/100–105, brief §2.18; handoff starter onboardingFlow.ts). Pure reducer so the
 * flow is testable and the Worker can validate the same rules on submit.
 *   1 who you are → 2 resident: address → ward (in the browser) | office: confirm the City email with a code + which
 *   office | advocacy: find your organization (join) or ask for a new one → 3 what you follow → issued
 * Nothing here grants access: residents are approved; team accounts are created by the Chicago Politics team, so an
 * office or advocacy choice becomes a request (onboarding/Onboarding.tsx, POST /api/dashboard/request-access).
 */
import type { UserGroup } from "../nav/navConfig";

export interface WardChoice { number: number; alderpersonId: string; alderpersonName: string }

export interface OnboardingState {
  step: 1 | 2 | 3;
  group: UserGroup | null;
  /** Team members already admitted to their team skip step 2 (their team is known). */
  skipWhere: boolean;
  // step 2 · residents
  address: string;
  ward: WardChoice | null;             // residents: from the address (wardLookup.ts); offices: "Which office?"
  countInWardTotals: boolean;          // "How would you vote?" in ward totals: opt-in, off until turned on
  // step 2 · offices
  workEmail: string;
  codeVerified: boolean;               // the 6-digit code sent to the City email
  // step 2 · advocacy
  org: { mode: "create" | "join"; name: string; id?: string } | null;
  // step 3
  topics: string[];
  follow: string[];                    // record ids
  digest: boolean;
}

export const initialOnboarding = (): OnboardingState => ({
  step: 1, group: null, skipWhere: false, address: "", ward: null, countInWardTotals: false, workEmail: "", codeVerified: false,
  org: null, topics: [], follow: [], digest: true,
});

export type OnboardingAction =
  | { t: "group"; group: UserGroup }
  | { t: "skipWhere"; on: boolean }
  | { t: "address"; address: string }
  | { t: "ward"; ward: WardChoice | null }
  | { t: "countInWardTotals"; on: boolean }
  | { t: "workEmail"; email: string }
  | { t: "codeVerified"; on: boolean }
  | { t: "org"; org: OnboardingState["org"] }
  | { t: "toggleTopic"; topic: string }
  | { t: "toggleFollow"; recordId: string }
  | { t: "digest"; on: boolean }
  | { t: "next" } | { t: "back" };

export const OFFICE_DOMAIN = "@cityofchicago.org";
export const isCityEmail = (email: string) => email.trim().toLowerCase().endsWith(OFFICE_DOMAIN);

/** Why the primary button is disabled (null = can continue). Read to screen readers; the screens show no hint text. */
export function blocker(s: OnboardingState): string | null {
  if (s.step === 1) return s.group ? null : "Choose who you're here as";
  if (s.step === 2) {
    if (s.group === "resident") return s.ward ? null : "Enter an address in Chicago to find your ward";
    if (s.group === "office") {
      if (!isCityEmail(s.workEmail)) return `Use your ${OFFICE_DOMAIN} email`;
      if (!s.ward) return "Choose your office";
      return null;
    }
    if (s.group === "advocacy") return s.org && s.org.name.trim().length > 1 ? null : "Find or create your organization";
  }
  if (s.step === 3) return s.topics.length + s.follow.length > 0 ? null : "Follow at least one topic or item";
  return null;
}

/** Step 2 (advocacy): the organization after the search text changes. Creating: the text is the new team's name;
 *  otherwise a chosen team stays only while the text still names it. */
export function orgForQuery(org: OnboardingState["org"], text: string, creating: boolean): OnboardingState["org"] {
  const name = text.trim();
  if (creating) return name.length > 1 ? { mode: "create", name } : null;
  return org && org.name.trim().toLowerCase() === name.toLowerCase() ? org : null;
}

const toggle = (xs: string[], x: string) => (xs.includes(x) ? xs.filter((y) => y !== x) : [...xs, x]);

export function onboardingReducer(s: OnboardingState, a: OnboardingAction): OnboardingState {
  switch (a.t) {
    // A different group's "where" doesn't carry over (a resident's ward isn't an office's ward).
    case "group": return a.group === s.group ? s : { ...s, group: a.group, ward: null, address: "", org: null, codeVerified: false };
    case "skipWhere": return { ...s, skipWhere: a.on };
    case "address": return { ...s, address: a.address, ward: null };       // ward must be re-resolved
    case "ward": return { ...s, ward: a.ward };
    case "countInWardTotals": return { ...s, countInWardTotals: a.on };
    case "workEmail": return { ...s, workEmail: a.email, codeVerified: a.email.trim().toLowerCase() === s.workEmail.trim().toLowerCase() && s.codeVerified };
    case "codeVerified": return { ...s, codeVerified: a.on };
    case "org": return { ...s, org: a.org };
    case "toggleTopic": return { ...s, topics: toggle(s.topics, a.topic) };
    case "toggleFollow": return { ...s, follow: toggle(s.follow, a.recordId) };
    case "digest": return { ...s, digest: a.on };
    case "next": return blocker(s) || s.step === 3 ? s : { ...s, step: (s.step === 1 && s.skipWhere ? 3 : s.step + 1) as 2 | 3 };
    case "back": return s.step === 1 ? s : { ...s, step: (s.step === 3 && s.skipWhere ? 1 : s.step - 1) as 1 | 2 };
  }
}

/** What we POST at the end. The street address is NOT sent: only the resolved ward. */
export const onboardingPayload = (s: OnboardingState) => ({
  group: s.group, ward: s.ward?.number ?? null, countInWardTotals: s.group === "resident" ? s.countInWardTotals : false,
  workEmail: s.group === "office" ? s.workEmail : undefined, org: s.group === "advocacy" ? s.org : undefined,
  topics: s.topics, follow: s.follow, digest: s.digest,
});

/**
 * Whether a signed-in person sees onboarding instead of the page they opened: always on /dashboard/welcome (Settings'
 * "Change"); otherwise only until they finish it. Chicago Politics staff never go through it (their team is the
 * platform team), and a team request waiting for approval shows the pending state instead.
 */
export function showOnboarding(me: { staff?: boolean; group?: string | null; access?: string | null },
  ob: { done: boolean; teamRequest?: { status?: string } | null }, section: string): boolean {
  if (section === "welcome") return true;
  if (ob.done || me.staff || ob.teamRequest?.status === "requested") return false;
  return !!me.group || (me.access ?? "none") === "none";
}
